import React, { useState, useRef, useEffect } from "react";

import "./css/combo_box.scss";
import TrainingTip from "../ui/training_tip";

/**
 * ComboBox: a searchable dropdown. Unlike a native <datalist>, clicking the
 * field shows the FULL list immediately (the LO does not have to delete the
 * current value first) and typing filters it. Free text is still allowed, so
 * custom lenders keep working.
 */
export default function ComboBox({
  id,
  name,
  fieldName,
  value,
  onChange,
  onCommit,
  options,
  title,
  placeholder,
  showRequired
}) {
  const [open, setOpen] = useState(false);
  // null = just focused (show the whole list); a string = the LO is filtering.
  const [search, setSearch] = useState(null);
  const ref = useRef();

  useEffect(() => {
    function handleOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch(null);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const query = search === null ? "" : search.toLowerCase();
  const filtered = (options || []).filter(o =>
    o.toLowerCase().includes(query)
  );

  function set(next) {
    if (onChange && fieldName) onChange({ [fieldName]: next });
  }

  function choose(option) {
    set(option);
    setOpen(false);
    setSearch(null);
  }

  return (
    <div
      className="input-field-container text-field-container combo-box"
      ref={ref}
    >
      <label htmlFor={id}>
        {name}
        {showRequired && <span className="required-asterisk">*</span>}
        {title && (
          <>
            {" "}
            <TrainingTip content={title} />
          </>
        )}
      </label>

      <input
        id={id}
        className="input-field text-field"
        type="text"
        autoComplete="off"
        value={value || ""}
        placeholder={placeholder}
        onFocus={e => {
          e.target.select();
          setSearch(null);
          setOpen(true);
        }}
        onClick={() => setOpen(true)}
        onChange={e => {
          setSearch(e.target.value);
          set(e.target.value);
          setOpen(true);
        }}
        onBlur={() => {
          // Commit the typed value (e.g. save a custom entry to the reusable
          // list). Choosing an option preventDefaults blur, so this fires only
          // for free-typed values.
          if (onCommit) onCommit(value);
        }}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === "Escape") {
            setOpen(false);
            if (e.key === "Enter" && onCommit) onCommit(value);
          }
        }}
      />

      {open && (
        <div className="combo-list">
          {filtered.length ? (
            filtered.map(option => (
              <button
                type="button"
                key={option}
                className={"combo-opt" + (option === value ? " sel" : "")}
                onMouseDown={e => {
                  e.preventDefault();
                  choose(option);
                }}
              >
                {option}
              </button>
            ))
          ) : (
            <div className="combo-empty">
              No match — keep typing to add a custom lender.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
